// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Lustre — the printed components: 56 spars in three tiers, 14 Patrons, and the hue rotation that
// makes concentration hard.
//
// THE COST ROTATION IS LOAD-BEARING. A spar of hue h costs only the OTHER three hues, at offsets
// a=h+1, b=h+2, c=h+3. So a pip in hue h discounts everything except hue h — piling into one hue
// buys you a big discount on three quarters of the board and nothing at all on the quarter you are
// standing in. That is why the Patron shapes price the way they do: {3,3} is worth more than
// {2,2,2} despite costing the same six rough spars, because two deep columns are harder to build
// than three shallow ones under a per-hue pip cap of 3.
//
// Nothing here is hidden. Both decks are shuffled from the shared seed and every face-up card is
// public, which is what lets the whole game be a pure replay of a move log with no host.

/** Hue indices. The glyph is the colour-independent signal and is used identically everywhere. */
export const GARNET = 0;
export const TOPAZ = 1;
export const MALACHITE = 2;
export const AZURITE = 3;
export const HUES = [GARNET, TOPAZ, MALACHITE, AZURITE] as const;
export const HUE_COUNT = 4;

export const HUE_NAME = ['Garnet', 'Topaz', 'Malachite', 'Azurite'];
export const HUE_GLYPH = ['▲', '◆', '●', '■'];
export const OPAL_GLYPH = '✶';

/** Per-hue pip cap. A fifth rough spar of a hue still drives the wheel; it grants no further pip. */
export const PIP_CAP = 3;
/** How many ROUGH spars fit on a bench. A cut spar leaves the count and frees the slot. */
export const ROUGH_CAP = 8;
/** Everything you hold, grit and Opals together. */
export const HAND_CAP = 12;
/** A pile must hold at least this many BEFORE you may take two from it. */
export const TAKE2_MIN = 5;
/** Shelved spars waiting to be claimed. */
export const SHELF_CAP = 2;

/** Grit in each hue pile, and Opals, by player count. Index by players. */
export const POOL_PER_HUE = [0, 5, 5, 6, 8];
export const OPAL_POOL = [0, 4, 4, 5, 6];

// ── the deck ────────────────────────────────────────────────────────────────────────────────────

export interface Tier {
  /** How much this spar drives the wheel while it is rough. */
  bite: number;
  /** Lustre gained by cutting it — and, because it is the same number, how far the dial moves. */
  cut: number;
  /** Copies per hue. */
  per: number;
  name: string;
  /** Cost patterns as [offset, count] pairs; offset 1/2/3 = the a/b/c hue relative to the spar's. */
  costs: Array<Array<[number, number]>>;
}

export const TIERS: Tier[] = [
  {
    name: 'Rough',
    bite: 1,
    cut: 2,
    per: 7,
    costs: [
      [[1, 3]],
      [
        [1, 2],
        [2, 1],
      ],
      [
        [1, 1],
        [2, 1],
        [3, 1],
      ],
      [
        [1, 2],
        [2, 2],
      ],
      [
        [1, 2],
        [2, 1],
        [3, 1],
      ],
      [[1, 4]],
      [
        [1, 3],
        [2, 1],
        [3, 1],
      ],
    ],
  },
  {
    name: 'Bright',
    bite: 2,
    cut: 3,
    per: 4,
    costs: [
      [[1, 5]],
      [
        [1, 3],
        [2, 3],
      ],
      [
        [1, 4],
        [2, 2],
        [3, 1],
      ],
      [
        [1, 3],
        [2, 2],
        [3, 2],
      ],
    ],
  },
  {
    name: 'Deep',
    bite: 3,
    cut: 6,
    per: 3,
    costs: [
      [
        [1, 5],
        [2, 3],
      ],
      [
        [1, 5],
        [2, 4],
      ],
      [
        [1, 4],
        [2, 3],
        [3, 3],
      ],
    ],
  },
];

export const TIER_COUNT = 3;
/** Face-up slots per tier row. */
export const LODE_SLOTS = 3;

export interface SparDef {
  tier: number;
  hue: number;
  /** cost[hue] = grit required. */
  cost: number[];
}

/** Every printed spar, in a fixed order. The seeded shuffle is what varies, never this. */
export function buildDeck(): SparDef[][] {
  const decks: SparDef[][] = [[], [], []];
  for (let t = 0; t < TIER_COUNT; t++) {
    const tier = TIERS[t];
    for (const hue of HUES) {
      for (const pattern of tier.costs) {
        const cost = [0, 0, 0, 0];
        for (const [offset, count] of pattern) cost[(hue + offset) % HUE_COUNT] = count;
        decks[t].push({ tier: t, hue, cost });
      }
    }
  }
  return decks;
}

/** Sanity constants the tests re-derive independently rather than importing. */
export const DECK_SIZE = [28, 16, 12];

// ── Patrons ─────────────────────────────────────────────────────────────────────────────────────

export interface PatronDef {
  /** Pips required, per hue. */
  need: number[];
  value: number;
  shape: '33' | '321' | '222';
}

/**
 * Fourteen Patrons, all costing exactly six rough spars and priced by how hard the SHAPE is to
 * build rather than by the spar count.
 *
 * A first pass priced {2,2,2} above {3,3}, which made six of the fourteen dead cards: identical cost,
 * strictly more points. Under the cost rotation two deep columns are the harder build, so they pay
 * more. The balance sim asserts that all three shapes are actually taken.
 */
export function buildPatrons(): PatronDef[] {
  const out: PatronDef[] = [];
  // {3,3} — every hue pair.
  for (let i = 0; i < HUE_COUNT; i++) {
    for (let j = i + 1; j < HUE_COUNT; j++) {
      const need = [0, 0, 0, 0];
      need[i] = 3;
      need[j] = 3;
      out.push({ need, value: 5, shape: '33' });
    }
  }
  // {3,2,1} — four rotations, so no hue is systematically easier.
  for (let r = 0; r < HUE_COUNT; r++) {
    const need = [0, 0, 0, 0];
    need[r % HUE_COUNT] = 3;
    need[(r + 1) % HUE_COUNT] = 2;
    need[(r + 2) % HUE_COUNT] = 1;
    out.push({ need, value: 4, shape: '321' });
  }
  // {2,2,2} — every hue triple.
  for (let skip = 0; skip < HUE_COUNT; skip++) {
    const need = [2, 2, 2, 2];
    need[skip] = 0;
    out.push({ need, value: 3, shape: '222' });
  }
  return out;
}

export const PATRON_COUNT = 14;
